import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import isLogged from 'utils/isLogged';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

type ClientSettings = {
  acceptNews: boolean;
  acceptMarketingEmails: boolean;
  acceptMarketingSms: boolean;
  acceptMarketingCalls: boolean;
  acceptDataSharing: boolean;
};

type AcceptNewsOptions = {
  body: {
    acceptNews: boolean;
  };
};

type AcceptNewsResponse = {
  identifiedBy: string;
  invoiceByMail: boolean;
  acceptNews: boolean;
  marketingCommunicationChannelControlEnabled: boolean;
  acceptMarketingEmails: boolean;
  acceptMarketingSms: boolean;
  acceptMarketingCalls: boolean;
  acceptProfiling: boolean;
  acceptDataSharing: boolean;
  acceptInternalDataSharing: boolean;
  language: string;
  identified: boolean;
  invoiceAddress: object;
  _links: object;
  _embedded: object;
};

type ClientSettingsOptions = {
  body: {
    acceptNews: boolean;
    acceptMarketingEmails: boolean;
    acceptMarketingSms: boolean;
    acceptMarketingCalls: boolean;
    acceptDataSharing: boolean;
  };
};

type ClientSettingsResponse = {
  identifiedBy: string;
  invoiceByMail: boolean;
  acceptNews: boolean;
  marketingCommunicationChannelControlEnabled: boolean;
  acceptMarketingEmails: boolean;
  acceptMarketingSms: boolean;
  acceptMarketingCalls: boolean;
  acceptProfiling: boolean;
  acceptDataSharing: boolean;
  acceptInternalDataSharing: boolean;
  language: string;
  identified: boolean;
  invoiceAddress: object;
  acceptNewsGrantedDate: string;
  _links: object;
  _embedded: object;
};

type ClientSettingsContextType = {
  clientSettings: ClientSettings | undefined | null;
  fetchClientSettings: () => Promise<ClientSettings | void>;
  updateAcceptNews: ({
    body,
  }: AcceptNewsOptions) => Promise<AcceptNewsResponse | void>;
  updateClientSettings: ({
    body,
  }: ClientSettingsOptions) => Promise<ClientSettingsResponse | void>;
};

const ClientSettingsContext = createContext<ClientSettingsContextType>(
  {} as ClientSettingsContextType,
);

const useClientSettings = (): ClientSettingsContextType =>
  useContext(ClientSettingsContext);

const ClientSettingsProvider = ({ children }: Props): JSX.Element => {
  const [clientSettings, setClientSettings] = useState<
    ClientSettings | undefined | null
  >();
  const { showError } = useError();
  const location = useLocation();

  const fetchClientSettings =
    useCallback(async (): Promise<ClientSettings | void> => {
      try {
        const fetchedClientSettings = await axiosInstance.get(
          '/client/settings',
        );
        setClientSettings(fetchedClientSettings.data);
        return fetchedClientSettings.data;
      } catch (e) {
        setClientSettings(null);
        showError();
        throw e;
      }
    }, [showError]);

  useEffect(() => {
    if (clientSettings === undefined && isLogged()) fetchClientSettings();
  }, [location]);

  const updateAcceptNews = useCallback(
    async ({ body }: AcceptNewsOptions): Promise<AcceptNewsResponse | void> => {
      try {
        const response = await axiosInstance.put(
          '/client/settings/accept-news',
          body,
        );

        setClientSettings(response.data);
        return response.data;
      } catch (e) {
        if (!axios.isAxiosError(e)) {
          showError();
          throw e;
        }
        // Don't throw error otherwise
      }
    },
    [showError],
  );

  const updateClientSettings = useCallback(
    async ({
      body,
    }: ClientSettingsOptions): Promise<ClientSettingsResponse | void> => {
      try {
        const response = await axiosInstance.put('/client/settings', body);

        setClientSettings(response.data);
        return response.data;
      } catch (e) {
        if (!axios.isAxiosError(e)) {
          showError();
          throw e;
        }
        // Don't throw error otherwise
      }
    },
    [showError],
  );

  const clientSettingsContextValue = useMemo(
    () => ({
      clientSettings,
      fetchClientSettings,
      updateAcceptNews,
      updateClientSettings,
    }),
    [
      clientSettings,
      fetchClientSettings,
      updateAcceptNews,
      updateClientSettings,
    ],
  );

  return (
    <ClientSettingsContext.Provider value={clientSettingsContextValue}>
      {children}
    </ClientSettingsContext.Provider>
  );
};

export { useClientSettings as default, ClientSettingsProvider };
